import { Link, useRouterState } from "@tanstack/react-router";

import { useCapabilities } from "../session/capabilities";

interface MoreItem {
  key: string;
  label: string;
  path: string;
  locked_at?: string[];
  stub?: boolean;
}

const MORE_ITEMS: MoreItem[] = [
  { key: "strings", label: "Strings & combiners", path: "strings", locked_at: ["easy_m"] },
  { key: "schematic", label: "Schematic", path: "schematic", locked_at: ["easy_m"] },
  // Weather (P10) and Settings (P11) are Tier M in the inventory and both screens
  // are built: Weather shows the measured tiles and locks the forecast in place with
  // its tier, Settings is the read-only register view. Marking them `stub` rendered
  // them as inert "not built yet" text, so two finished Easy M pages had no way in.
  { key: "weather", label: "Weather & forecast", path: "weather" },
  { key: "datapath", label: "Data path & coverage", path: "datapath" },
  { key: "settings", label: "Settings", path: "settings" },
];

export const MORE_PATHS = MORE_ITEMS.map((i) => i.path);

interface SiteMoreMenuProps {
  basePath: string;
  onClose: () => void;
}

export function SiteMoreMenu({ basePath, onClose }: SiteMoreMenuProps) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const caps = useCapabilities();

  return (
    <>
      <button
        type="button"
        aria-label="Close menu"
        style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 2 }}
        onClick={onClose}
      />
      <div className="menu">
        {MORE_ITEMS.map((item) => {
          if (item.locked_at?.includes(caps.tier)) {
            const tierName = caps.tier === "easy_m" ? "Easy T" : caps.tier === "easy_t" ? "Easy U" : undefined;
            return (
              <div key={item.key} style={lockedStyle}>
                {item.label}
                {tierName ? <span style={tierBadge}>{tierName}</span> : null}
              </div>
            );
          }
          if (item.stub) {
            return (
              <div key={item.key} style={stubStyle} title={`${item.label} — not built yet`}>
                {item.label}
              </div>
            );
          }
          const href = `${basePath}/${item.path}`;
          const active = pathname.startsWith(href);
          return (
            <Link
              key={item.key}
              to={href as never}
              onClick={onClose}
              style={{ textDecoration: "none" }}
            >
              <div className={active ? "on" : ""}>{item.label}</div>
            </Link>
          );
        })}
      </div>
    </>
  );
}

const lockedStyle: React.CSSProperties = {
  opacity: 0.55,
  cursor: "default",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const stubStyle: React.CSSProperties = {
  opacity: 0.45,
  cursor: "default",
  borderBottom: "1px dashed var(--sk-color-border-2)",
  display: "inline-block",
};

const tierBadge: React.CSSProperties = {
  fontSize: 9,
  lineHeight: 1,
  padding: "1px 4px",
  borderRadius: 3,
  background: "var(--sk-color-surface-2)",
  color: "var(--sk-color-ink-3)",
  fontWeight: 500,
};
